import { classname } from "@jauntin/utilities";
import { RxMembershipSignUpProp } from "Features/MembershipSignUp/Rx/RxMembershipSignUp/RxMembershipSignUp";
import {
  isSummaryEmpty,
  getSummary,
  getSelectedPlan,
} from "Features/MembershipSignUp/Rx/RxMembershipSignUp/Selectors/summary.selector";
import { getDependentsList } from "Features/MembershipSignUp/Rx/RxMembershipSignUp/Selectors/dependents.selector";
import { ReactComponent as EnvelopeIcon } from "@basicare/common/src/Assets/Images/envelope-icon.svg";
import { ReactComponent as PhoneIcon } from "@basicare/common/src/Assets/Images/phone-icon.svg";
import { currencyFromInt } from "@basicare/common/src/Helpers/CurrencyHelper";
import { getValue } from "Features/MembershipSignUp/Rx/RxMembershipSignUp/Selectors/form.selector";
import { Gender } from "@basicare/common/src/Constants/genders";
import { BCX_EMAIL, BCX_PHONE } from "Constants/bcxContact";

const Summary = ({
  rxMembershipSignUp,
  fullScreen = false,
  close,
}: {
  rxMembershipSignUp: RxMembershipSignUpProp;
  fullScreen?: boolean;
  close?: () => void;
}) => {
  const [$state] = rxMembershipSignUp;

  const isEmpty = isSummaryEmpty($state);
  const summary = getSummary($state);
  const selectedPlan = getSelectedPlan($state);

  const dependentsList = getDependentsList($state);

  return (
    <div className={classname("summary", fullScreen && "summary--fullScreen")}>
      <div className="summary_wrapper">
        <div
          className={classname(
            "summary__header d-flex align-items-center",
            close && "summary__header--sticky"
          )}
        >
          <h2 className="mb-0">SUMMARY</h2>
          {fullScreen && (
            <div className="ml-auto d-none d-lg-block">
              <span className="bold-1 c-primary">
                Complete Care Bundle Subscription Interval:{" "}
              </span>
              {summary["Subscription Interval"]}
            </div>
          )}

          {close && (
            <button className="btn btn-link ml-auto py-0" onClick={close}>
              <i className="fas fa-chevron-down summary-drawer__arrow c-primary"></i>
            </button>
          )}
        </div>
        {fullScreen && (
          <>
            <div className="summary__line pt-4 mb-0 d-none d-lg-block">
              <div className="summary__line-title c-primary">
                Primary Member
              </div>
            </div>
          </>
        )}
        <div
          className={classname(
            "summary__primary-info",
            fullScreen && "summary__primary-info--fullScreen"
          )}
        >
          {isEmpty && (
            <div className="summary__text--empty">
              As you fill out the form, your order details will appear here.
            </div>
          )}
          {fullScreen ? (
            <>
              <div className="summary__line d-lg-none">
                <div className="summary__line-title">
                  <span className="bold-1 c-primary">
                    Complete Care Bundle Subscription Interval:{" "}
                  </span>
                  <span className="summary__line-text font-weight-normal">
                    {summary["Subscription Interval"]}
                  </span>
                </div>
              </div>
              <div className="summary__line d-lg-none">
                <div className="summary__line-title c-primary">
                  Primary Member
                </div>
              </div>
              <div className="summary__line row">
                <div className="col-6 col-xl-auto mw-xl--200">
                  <div className="summary__line-title">First Name</div>
                  <div className="summary__line-text">
                    {getValue($state, "generalInformation.firstName") as string}
                  </div>
                </div>
                <div className="col-6 col-xl-auto mw-xl--200">
                  <div className="summary__line-title">Last Name</div>
                  <div className="summary__line-text">
                    {getValue($state, "generalInformation.lastName") as string}
                  </div>
                </div>
              </div>
              <div className="summary__line row">
                <div className="col-6 col-lg-auto">
                  <div className="summary__line-title">Date of Birth</div>
                  <div className="summary__line-text">
                    {
                      getValue(
                        $state,
                        "generalInformation.dateOfBirth"
                      ) as string
                    }
                  </div>
                </div>
                <div className="col-6 col-lg-auto">
                  <div className="summary__line-title">Gender</div>
                  <div className="summary__line-text">
                    {(getValue(
                      $state,
                      "generalInformation.gender"
                    ) as string) === Gender.Male
                      ? "Male"
                      : "Female"}
                  </div>
                </div>
              </div>
              {Object.keys(summary)
                .filter((item, i) => i > 3)
                .map((item, i) => (
                  <div key={i} className="summary__line">
                    <div className="summary__line-title">{item}</div>
                    <div className="summary__line-text">{summary[item]}</div>
                  </div>
                ))}
            </>
          ) : (
            <>
              {Object.keys(summary).map((item, i) => (
                <div key={i} className="summary__line">
                  <div className="summary__line-title">{item}</div>
                  <div className="summary__line-text">{summary[item]}</div>
                </div>
              ))}
            </>
          )}
        </div>
        {dependentsList.length > 0 && (
          <div
            className={classname(
              "summary__dependents d-flex flex-column flex-xl-row flex-wrap",
              fullScreen ? "" : "d-lg-none"
            )}
          >
            {dependentsList.map(
              (
                {
                  dependent: {
                    relationshipToAccountHolder,
                    firstName,
                    lastName,
                    gender,
                    dateOfBirth,
                  },
                },
                index
              ) => (
                <div key={index} className="summary__dependent col col-xl-6">
                  <div className="summary__line-title c-primary mb-2">
                    {relationshipToAccountHolder}
                  </div>
                  <div className="summary__dependent-row row">
                    <div className="col-6 col-lg-3">
                      <div className="summary__line-title">First Name</div>
                      <div className="summary__line-text">{firstName}</div>
                    </div>
                    <div className="col-6 col-lg-3">
                      <div className="summary__line-title">Last Name</div>
                      <div className="summary__line-text">{lastName}</div>
                    </div>
                    <div className="col-6 col-lg-3">
                      <div className="summary__line-title">Date of Birth</div>
                      <div className="summary__line-text">{dateOfBirth}</div>
                    </div>
                    <div className="col-6 col-lg-3">
                      <div className="summary__line-title">Gender</div>
                      <div className="summary__line-text">
                        {gender === Gender.Male ? "Male" : "Female"}
                      </div>
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        )}
        <div
          className={classname(
            "summary__selected-plan",
            fullScreen && "summary__selected-plan--fullScreen"
          )}
        >
          <div className="summary__plan-text">
            Total{" "}
            {selectedPlan && (
              <span className="text-capitalize">{selectedPlan.planType} </span>
            )}
            Cost:
          </div>
          <div
            className={`summary__plan-price ${selectedPlan?.planType || ""}`}
          >
            {selectedPlan
              ? currencyFromInt(selectedPlan?.price).formatDollars()
              : "$ --"}
          </div>
        </div>
        <div className="summary__footer">
          <div className="summary__footer-line mb-3">
            <div className="summary__icon-circle">
              <EnvelopeIcon />
            </div>
            <div>
              Message us at <a href={`mailto:${BCX_EMAIL}`}>{BCX_EMAIL}</a>
            </div>
          </div>
          <div className="summary__footer-line">
            <div className="summary__icon-circle">
              <PhoneIcon />
            </div>
            <div>
              Or call us at <a href={`tel:${BCX_PHONE}`}>{BCX_PHONE}</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Summary;
